/* eslint-disable max-len */
import React, {
    useState, useContext, useCallback, useEffect
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { TranslatorContext } from '@jutro/locale';
import { useValidation } from 'gw-portals-validation-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { useAuthentication } from 'gw-digital-auth-react';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import { ViewModelServiceContext, ViewModelForm } from 'gw-portals-viewmodel-react';
import { ModalNextProvider } from '@jutro/components';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import Address from '../../models/Address';
import metadata from './LossLocation.metadata.json5';
import styles from './LossLocation.module.scss';
import messages from './LossLocation.messages';
import fnolmessages from '../../../gw-capability-fnol-ca-react/FNOLCA.messages';

function FNOLLossLocation(props) {
    const {
        id, onValidate, path: claimVM, updatePath: updateWizardData
    } = props;
    const translator = useContext(TranslatorContext);
    const { authHeader } = useAuthentication();
    const { AddressLookupService } = useDependencies('AddressLookupService');

    const viewModelService = useContext(ViewModelServiceContext);
    const [lossLocationAddress, updateLossLocationAddress] = useState('');
    const [policyAddressDropdown, updatePolicyAddressDropdown] = useState();
    const {
        isComponentValid,
        registerComponentValidation
    } = useValidation(id);
    const addressViewModel = viewModelService.create(
        _.get(claimVM.value, 'lossLocation') || new Address(),
        'cc',
        'edgev1141.capabilities.address.dto.AddressDTO'
    );
    const [addressVM] = useState(addressViewModel);
    const [addressChange, setAddressChange] = useState({
        predefinedAddress: true,
        exactAddress: false,
        locDescOnly: false,
        map: false
    });
    const [lookupResult_Ext, updatelookupResult_Ext] = useState(null);
    const [showLookupResult_Ext, updateShowLookupResult_Ext] = useState(false);
    const [showPostCodeLoader, updateshowPostCodeLoader] = useState(false);
    const handleAddressDropDownValueChange = useCallback(
        (addressIndex) => {
            const updatedLocations = claimVM.predefinedLossLocations.value.find((loss, index) => {
                return index === parseInt(addressIndex, 10);
            });
            _.set(claimVM.value, 'optionalLossLocation.predefinedAddress', updatedLocations);
            updatePolicyAddressDropdown(addressIndex);
            addressVM.value = _.omit(updatedLocations, 'publicID');
            _.set(claimVM, 'lossLocation.value', updatedLocations);
            updateWizardData(claimVM);
        },
        [addressVM, claimVM, updateWizardData]
    );

    const handleValueChange = useCallback(
        (value) => {
            updateLossLocationAddress(value);

            const isNonSpecificLocation = (value === 'locDescOnly' || value === 'predefinedAddress');
            const isGeoCoded_Ext = value === 'map';
            const isLossLocation_Ext = value === 'exactAddress';
            const previouSelectedAddress = _.get(claimVM.value, 'selectedAddress');
            const isResetAddressValue = (previouSelectedAddress !== value) && !(previouSelectedAddress === 'map' && value === 'exactAddress');
            _.set(claimVM.value, 'selectedAddress', value);
            const optionalLossLocationObject = _.get(claimVM.value, 'optionalLossLocation');
            const lossLocationValue = _.get(claimVM.value, 'lossLocation');
            if (
                (_.isEmpty(optionalLossLocationObject) && _.isEmpty(lossLocationValue))
                || (value === 'exactAddress' && isResetAddressValue)
                || (value === 'locDescOnly' && isResetAddressValue)
                || (value === 'map' && isResetAddressValue)
            ) {
                const optionalLossLocation = {
                    predefinedAddress: new Address(),
                    others: new Address()
                };
                _.set(claimVM.value, 'optionalLossLocation', optionalLossLocation);
                addressVM.value = value === 'predefinedAddress'
                    ? optionalLossLocation.predefinedAddress
                    : optionalLossLocation.others;
                _.set(claimVM.value, 'lossLocation', addressVM.value);
            } else if (!_.isEmpty(lossLocationValue)) {
                addressVM.value = _.omit(lossLocationValue, 'publicID');
                _.set(claimVM.value, 'lossLocation', addressVM.value);
                if (value === 'predefinedAddress' && policyAddressDropdown) {
                    handleAddressDropDownValueChange(policyAddressDropdown);
                }
            } else {
                addressVM.value = value === 'predefinedAddress'
                    ? optionalLossLocationObject.predefinedAddress
                    : optionalLossLocationObject.others;
                _.set(claimVM.value, 'lossLocation', addressVM.value);
            }
            if(!(addressVM.value.isGeoCoded_Ext && value === 'map')){
                addressVM.value.isNonSpecificLocation = isNonSpecificLocation;
                _.set(claimVM.value, 'lossLocation.isNonSpecificLocation', isNonSpecificLocation);
                addressVM.value.isGeoCoded_Ext = isGeoCoded_Ext;
                _.set(claimVM.value, 'lossLocation.isGeoCoded_Ext', isGeoCoded_Ext);
                addressVM.value.isLossLocation_Ext = isLossLocation_Ext;
                _.set(claimVM.value, 'lossLocation.isLossLocation_Ext', isLossLocation_Ext);
                addressVM.value.geoLatitude_Ext = undefined;
                addressVM.value.geoLongitude_Ext = undefined;
            }
            updateWizardData(claimVM);
            switch (value) {
                case 'predefinedAddress':
                    setAddressChange({
                        predefinedAddress: true,
                        exactAddress: false,
                        locDescOnly: false,
                        map: false
                    });
                    break;
                case 'exactAddress':
                    setAddressChange({
                        predefinedAddress: false,
                        exactAddress: true,
                        locDescOnly: false,
                        map: false
                    });
                    break;
                case 'locDescOnly':
                    setAddressChange({
                        predefinedAddress: false,
                        exactAddress: false,
                        locDescOnly: true,
                        map: false
                    });
                    break;
                case 'map':
                    setAddressChange({
                        predefinedAddress: false,
                        exactAddress: false,
                        locDescOnly: false,
                        map: true
                    });
                    break;
                default:
                    setAddressChange({
                        predefinedAddress: true,
                        exactAddress: false,
                        locDescOnly: false,
                        map: false
                    });
                    break;
            }

        },
        [
            addressVM.value,
            claimVM,
            handleAddressDropDownValueChange,
            policyAddressDropdown,
            updateWizardData
        ]
    );

    useEffect(() => {
        registerComponentValidation(() => {
            return addressVM.aspects.valid && addressVM.aspects.subtreeValid;
        });
    }, [addressVM.aspects.subtreeValid, addressVM.aspects.valid, registerComponentValidation]);

    const componentRendered = useCallback(
        (defaultAddress) => {
            const prevLossLocationType = _.get(claimVM.value, 'selectedAddress');
            updateLossLocationAddress(prevLossLocationType || defaultAddress);
            handleValueChange(prevLossLocationType || defaultAddress);
        },
        [claimVM]
    );

    const setInitialAddress = useCallback(() => {
        // if (claimVM && claimVM.value.predefinedLossLocations.length > 0) {
        //     return componentRendered('predefinedAddress');
        // }
        return componentRendered('exactAddress');
    }, [claimVM, componentRendered]);

    useEffect(() => {
        setInitialAddress();
        // if (
        //     claimVM
        //     && lossLocationAddress === 'predefinedAddress'
        //     && claimVM.predefinedLossLocations.value.length === 1
        // ) {
        //     handleAddressDropDownValueChange(0);
        // }
        // All dependency is not required here. If we add all looping and hang the page.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidate]);

    useEffect(() => {
        return function cleanup() {
            // Removing manually loaded google map api to fix the following error.
            // "You have included the Google Maps JavaScript API multiple times on this page.
            // This may cause unexpected errors."
            // Since we are using JUTRO MapArea component here we dont need manually loaded script.

            if (window.googleMapsAPILoadedManually && _.get(addressChange, 'map')) {
                window.google = null;
                window.googleMapsAPILoadedManually = false;
            }
        };
    }, [addressChange]);

    const writeValue = useCallback(
        (value, path) => {
            _.set(addressVM, path, value);
            _.set(claimVM.value, 'lossLocation', addressVM.value);
            _.set(claimVM.value, 'optionalLossLocation.others', addressVM.value);
            updateWizardData(claimVM);
        },
        [claimVM, addressVM, updateWizardData]
    );

    const getAddressLineFields = useCallback(
        () => {
            return (
                <div>
                    <p>{_.get(addressVM.value, 'addressLine1') || ''}</p>
                    <p>{_.get(addressVM.value, 'addressLine2') || ''}</p>
                    <p>{_.get(addressVM.value, 'addressLine3') || ''}</p>
                </div>
            );
        },
        [addressVM]
    );

    const getLossLocationCodes = useCallback(() => {
        const filteredLocationCodes = _.filter(addressVM.lossLocationCode_Ext.aspects.availableValues,
            (typeCode) => {
                return _.includes(['NOI', 'ENG', 'SCT', 'WAL'], typeCode.code);
            });
        return filteredLocationCodes.map((locationCode) => {
            return {
                code: locationCode.code,
                name: translator({
                    id: locationCode.name,
                    defaultMessage: locationCode.name
                })
            };
        });
    }, [addressVM]);

    const getMultipleLookupResult = useCallback(() => {
        const locationCodes = lookupResult_Ext && lookupResult_Ext.resultSummaries;
        if (locationCodes) {
            return locationCodes.map((locationCode) => {
                return {
                    code: locationCode.addressSummary,
                    name: locationCode.addressSummary
                };
            });
        } else {
            return [];
        }

    }, [addressVM, lookupResult_Ext]);

    const lookupClick = useCallback(async () => {
        const newAddress = new Address();
        newAddress.postalCode = addressVM.postalCode.value;
        await AddressLookupService.addressLookup(newAddress, authHeader)
            .then((lookupResult) => {
                // TODO Handle for multiple addresses
                if (lookupResult.fetchRequired) {
                    updatelookupResult_Ext(lookupResult);
                    updateShowLookupResult_Ext(true);
                } else {
                    updateShowLookupResult_Ext(false);
                    addressVM.value = lookupResult.address;
                    addressVM.value.isGeoCoded_Ext = false;
                    addressVM.value.isLossLocation_Ext = true;
                    addressVM.value.isNonSpecificLocation = false;
                    _.set(claimVM, 'lossLocation.value', lookupResult.address);
                }
                updateWizardData(claimVM);
            }).catch(() => {
                addressVM.showLookupLoading = false;
                ModalNextProvider.showAlert({
                    title: fnolmessages.postCodeLookupErrorTitle,
                    message: translator(fnolmessages.postCodeLookupErrorMessage),
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: fnolmessages.postCodeLookupErrorConfirmMessage
                }).catch(_.noop);
            });
    }, [addressVM, authHeader, updateWizardData, AddressLookupService, translator]);

    const handleMultipleAddressDropDownValueChange = useCallback(async (addressIndex) => {
        updateshowPostCodeLoader(true);
        const updatedLocations = lookupResult_Ext.resultSummaries.find((location, index) => {
            return location.addressSummary === addressIndex;
        });
        const newAddress = new Address();
        newAddress.postalCode = addressVM.postalCode.value;
        if (updatedLocations) {
            await AddressLookupService.getAddressFromLookup(updatedLocations, newAddress, authHeader)
                .then((lookupResult) => {
                    addressVM.value = lookupResult;
                    addressVM.value.isGeoCoded_Ext = false;
                    addressVM.value.isLossLocation_Ext = true;
                    addressVM.value.isNonSpecificLocation = false;
                    _.set(claimVM, 'lossLocation.value', lookupResult);
                    updateWizardData(claimVM);
                    updateshowPostCodeLoader(false);
                }).catch(() => {
                    addressVM.showLookupLoading = false;
                    updateshowPostCodeLoader(false);
                    ModalNextProvider.showAlert({
                        title: fnolmessages.postCodeLookupErrorTitle,
                        message: translator(fnolmessages.postCodeLookupErrorMessage),
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: fnolmessages.postCodeLookupErrorConfirmMessage
                    }).catch(_.noop);
                });
        } else {
            addressVM.value = newAddress;
            updateshowPostCodeLoader(false);
        }
    },
    [addressVM, authHeader, updateWizardData, AddressLookupService, translator, lookupResult_Ext]);

    const isUnitedKingdomChosen = () => {
        return addressVM.country.value && addressVM.country.value.code === 'GB';
    };

    const isPostCodeValid = () => {
        if (!_.isUndefined(addressVM) && !_.isUndefined(addressVM.postalCode) && !_.isEmpty(addressVM.postalCode.value)) {
            const isValid = addressVM.postalCode.aspects.valid && addressVM.postalCode.aspects.subtreeValid;
            return !isValid;
        }
        return true;
    };

    const handleUserLocationChange = useCallback(
        (geocodingResult, path) => {
            debugger;
            const address = geocodingResult.translatedAddress;
            if (address.countryCode !== 'GB') {
                address.postalCode = '';
            }
            else {
                addressVM.value = new Address();
                addressVM.value.isNonSpecificLocation = true;
            }
            addressVM.value.isLossLocation_Ext = false;
            addressVM.value.isGeoCoded_Ext = true;
            if(geocodingResult.position && geocodingResult.position.lat){
                addressVM.value.geoLatitude_Ext = geocodingResult.position.lat.toFixed(7).toString();
                addressVM.value.geoLongitude_Ext = geocodingResult.position.lng.toFixed(7).toString();
            }
            _.set(claimVM, path, addressVM.value);
            updateWizardData(claimVM);
        },
        [updateWizardData, claimVM, addressVM]
    );
    const getGeoCodes = (item, index, property) => {
        return (
            <span>{item[property.id]}</span>
        );
    };

    const isLatitideValid = useCallback(
        (latitudeValue) => {
            const isLatitudeValid = isFinite(latitudeValue) && Math.abs(latitudeValue) <= 90;
            if(!isLatitudeValid){
                addressVM.value.geoLatitude_Ext = '';
                _.set(claimVM.value, 'geoLatitude_Ext', '');
                updateWizardData(claimVM);
            }else {
                let trimmedVlaue = latitudeValue;
                if(latitudeValue && latitudeValue.length >10){
                    trimmedVlaue = latitudeValue.slice(0, 10).toString();
                }
                addressVM.value.geoLatitude_Ext = trimmedVlaue;
                _.set(claimVM.value, 'geoLatitude_Ext', trimmedVlaue);
                updateWizardData(claimVM);
            }
            //if(latitudeValue.length)
        },
        [updateWizardData, claimVM, addressVM]
    );
    const isLongitudeValid = useCallback(
        (longitudeValue) => {
            const isLongitudeValid =  isFinite(longitudeValue) && Math.abs(longitudeValue) <= 180;
            if(!isLongitudeValid){
                addressVM.value.geoLongitude_Ext = '';
                _.set(claimVM.value, 'geoLongitude_Ext', '');
                updateWizardData(claimVM);
            }else {
                let trimmedVlaue = longitudeValue;
                if(longitudeValue && longitudeValue.length >10){
                     trimmedVlaue = longitudeValue.slice(0, 10).toString();
                }
                addressVM.value.geoLongitude_Ext = trimmedVlaue;
                _.set(claimVM.value, 'geoLongitude_Ext', trimmedVlaue);
                updateWizardData(claimVM);
            }
        },
        [updateWizardData, claimVM, addressVM]
    );

    const getAddressAvailableValues = () => {
        const addressOptionsArray = [
            // {
            //     code: 'predefinedAddress',
            //     name: translator(messages.fnolUseMyPolicyAddress)
            // },
            {
                code: 'exactAddress',
                name: translator(messages.fnolSpecifyFullAddress)
            },
            {
                code: 'locDescOnly',
                name: translator(messages.lossLocationDesc)
            },

            // Disabled for GoogleMaps Key unavailability
            {
                code: 'map',
                name: translator(messages.fnolLocationMap)
            }
        ];
        // if (claimVM
        //     && claimVM.value.predefinedLossLocations
        //     && claimVM.value.predefinedLossLocations.length === 0
        // ) {
        //     return addressOptionsArray.splice(1);
        // }
        return addressOptionsArray;
    };


    const overrideProps = {
        '@field': {
            labelPosition: 'left',
            showOptional: false
        },
        whereDidHappenRadioButton: {
            availableValues: getAddressAvailableValues(),
            value: lossLocationAddress
        },
        exactLocationAddressContainer: {
            visible: addressChange.exactAddress
        },
        PostcodeInlineLoader: {
            loading: showPostCodeLoader,
            loadingMessage: 'Fetching address...',
            visible: showPostCodeLoader
        },
        LossLocationOnPostcode: {
            label: 'Addresses for ' + addressVM.postalCode.value,
            visible: addressChange.exactAddress && showLookupResult_Ext && addressVM.postalCode.value
                && addressVM.postalCode.aspects.valid && addressVM.postalCode.aspects.subtreeValid,
            availableValues: getMultipleLookupResult(),
            onValueChange: (addressIndex) => {
                handleMultipleAddressDropDownValueChange(addressIndex);
            }
        },

        addressLines: {
            value: getAddressLineFields()
        },
        exactAddressLine1: {
            visible: lossLocationAddress === 'exactAddress' && addressChange.exactAddress
        },
        exactAddressLine2: {
            visible: lossLocationAddress === 'exactAddress' && addressChange.exactAddress
        },
        exactAddressLine3: {
            visible: lossLocationAddress === 'exactAddress' && addressChange.exactAddress
        },
        exactZipCode: {
            visible: lossLocationAddress === 'exactAddress' && addressChange.exactAddress && isUnitedKingdomChosen()
        },
        exactLossLocationCode: {
            availableValues: getLossLocationCodes(),
            visible: lossLocationAddress === 'exactAddress' && addressChange.exactAddress && isUnitedKingdomChosen()
        },
        exactGeoLatitude: {
            visible: addressChange.map,
            onValueChange: (value)=>{
                isLatitideValid(value);
            }
        },
        exactGeoLongitude: {
            visible: addressChange.map,
            onValueChange: (value)=>{
                isLongitudeValid(value);
            }
        },
        mapContainer: {
            visible: addressChange.map
        },
        googleMapPlacement: {
            googleMapsApiKey: appConfig.credentials.googleMapsApiKey,
            defaultValue: addressVM.value.geoLatitude_Ext ? `${addressVM.value.geoLatitude_Ext} , ${addressVM.value.geoLongitude_Ext}`:'Charing Cross Station, London WC2N 5RJ, UK',
            isMarker: addressChange.map && (addressVM.value.geoLatitude_Ext !==null && addressVM.value.geoLatitude_Ext !==undefined)
        },
        selectdLocation:{
            content: (addressChange.map && addressVM.value.city) ? `Chosen location  ${addressVM.value.addressLine1 || ''}  ${addressVM.value.addressLine2 || ''}  ${addressVM.value.city || ''} `
            : '',

        },
        mapGeolocationTable:{
            data: [{"col1":"Chosen location","col3":"Geo latitude","col4":addressVM.value.geoLatitude_Ext},{"col3":"Geo longitude", "col4":addressVM.value.geoLongitude_Ext}],
            visible: !_.isEmpty(addressVM.value.geoLatitude_Ext) && !_.isEmpty(addressVM.value.geoLongitude_Ext)

        },
        locationDescriptionOnlyContainer: {
            visible: addressChange.locDescOnly
        },
        locationDescriptionOnly: {
            visible: addressChange.locDescOnly
        },
        postCodeLookupButton: {
            disabled: isPostCodeValid() && isUnitedKingdomChosen(),
            visible: lossLocationAddress === 'exactAddress' && addressChange.exactAddress && isUnitedKingdomChosen()
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            handleValueChange: handleValueChange,
            onHandleAddressChange: handleAddressDropDownValueChange,
            onMapLocationChange: handleUserLocationChange,
            lookupClick: lookupClick,
            getGeoCodes:getGeoCodes,
        }
    };

    const readValue = useCallback(
        (fieldId, path) => {
            return readViewModelValue(
                metadata.pageContent,
                addressVM,
                fieldId,
                path,
                overrideProps
            );
        },
        [addressVM, overrideProps]
    );

    if (_.isEmpty(addressVM)) {
        return null;
    }

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={addressVM}
            overrideProps={overrideProps}
            resolveValue={readValue}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
            onValueChange={writeValue}
        />
    );
}
FNOLLossLocation.propTypes = {
    id: PropTypes.string.isRequired,
    path: PropTypes.shape({}).isRequired,
    onValidate: PropTypes.func.isRequired,
    updatePath: PropTypes.func.isRequired
};
export default FNOLLossLocation;
